/**
 * ไฟล์ Excel "สรุปรายงาน" ของหน้า "สรุปมาสาย" — 1 ชีตต่อแผนก คอลัมน์ รหัส · ชื่อ · สาย · ขาด · ลาแต่ละประเภท · รวมลา.
 * ใช้ข้อมูลชุดเดียวกับตารางบนจอ (GET /hr/late-report) ไม่ยิงคำขอใหม่ ตัวเลขในไฟล์จึงตรงกับจอเสมอ.
 * ส่วนที่เป็นตรรกะล้วน (ชื่อชีต / จัดกลุ่ม / แถวในชีต) แยกออกมาเพื่อเทสได้โดยไม่ต้องโหลด xlsx.
 */
import type { LateReportRow, LeaveColumn } from '../hooks';

/** Excel จำกัดชื่อชีต 31 ตัวอักษร */
export const SHEET_NAME_MAX = 31;
const FORBIDDEN = /[\\/?*[\]:]/g;
export const NO_DEPARTMENT = 'ไม่ระบุแผนก';

function cut(s: string, max: number): string {
  if (s.length <= max) return s;
  let out = s.slice(0, max);
  // ไม่ตัดกลางอักขระที่กินสองหน่วย (เช่นอีโมจิ) — เหลือครึ่งตัวแล้วไฟล์เพี้ยน
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
}

/** ชื่อชีตที่ Excel รับ: ตัดอักขระต้องห้าม \ / ? * [ ] : · ห้ามขึ้นต้น/ลงท้ายด้วย ' · ไม่เกิน 31 · ห้ามว่าง */
export function safeSheetName(raw: string | null | undefined): string {
  let s = (raw ?? '').replace(FORBIDDEN, ' ').replace(/\s+/g, ' ').trim();
  s = s.replace(/^'+|'+$/g, '').trim();
  s = cut(s, SHEET_NAME_MAX).trim();
  return s || NO_DEPARTMENT;
}

/**
 * แปลงชื่อแผนกทั้งชุดเป็นชื่อชีตที่ไม่ซ้ำกัน (Excel เทียบชื่อแบบไม่สนตัวพิมพ์เล็ก/ใหญ่)
 * ชื่อที่ชนกันหลังตัด/ล้างอักขระ ต่อท้าย " (2)", " (3)" โดยยังไม่เกิน 31 ตัว.
 * "History" เป็นชื่อสงวนของ Excel จึงนับว่าถูกใช้ไปแล้ว.
 */
export function uniqueSheetNames(rawNames: Array<string | null | undefined>): string[] {
  const used = new Set<string>(['history']);
  return rawNames.map((raw) => {
    const base = safeSheetName(raw);
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n++) {
      const suffix = ` (${n})`;
      name = `${cut(base, SHEET_NAME_MAX - suffix.length).trim()}${suffix}`;
    }
    used.add(name.toLowerCase());
    return name;
  });
}

export interface DepartmentGroup {
  department: string;
  rows: LateReportRow[];
}

/** จัดกลุ่มตามแผนก เรียงชื่อแผนกตามภาษาไทย ("ไม่ระบุแผนก" ไว้ท้าย) และเรียงคนตามรหัสในแต่ละแผนก */
export function groupByDepartment(rows: LateReportRow[]): DepartmentGroup[] {
  const map = new Map<string, LateReportRow[]>();
  for (const r of rows) {
    const dept = r.department?.trim() || NO_DEPARTMENT;
    const list = map.get(dept) ?? [];
    list.push(r);
    map.set(dept, list);
  }
  return [...map.entries()]
    .sort(([a], [b]) => {
      if (a === NO_DEPARTMENT) return 1;
      if (b === NO_DEPARTMENT) return -1;
      return a.localeCompare(b, 'th');
    })
    .map(([department, list]) => ({
      department,
      rows: [...list].sort((x, y) => x.emp_code.localeCompare(y.emp_code, 'th', { numeric: true })),
    }));
}

/** หัวตารางในชีต: ลาแต่ละประเภทตามที่ worker ส่งมา แล้วปิดท้ายด้วยรวมลา */
export function sheetColumns(leaveColumns: LeaveColumn[]): string[] {
  return ['รหัส', 'ชื่อ', 'สาย (ครั้ง)', 'สาย (นาที)', 'ขาด (วัน)', ...leaveColumns.map((c) => `${c.label} (วัน)`), 'รวมลา (วัน)'];
}
type Cell = string | number;

function formatThaiDate(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(
    new Date(`${iso}T00:00:00+07:00`),
  );
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * แถวทั้งหมดของชีตหนึ่งแผนก: หัวชีต 3 บรรทัด (บริษัท/แผนก/ช่วงวันที่) · หัวตาราง · คนในแผนก · แถวรวม · หมายเหตุ.
 * headerRow = index ของหัวตาราง (0-based) ให้ตัวสร้างไฟล์จัดความกว้าง/ตรึงแถวได้.
 */
export function buildDepartmentSheet(input: {
  company: string;
  department: string;
  from: string;
  to: string;
  leaveColumns: LeaveColumn[];
  rows: LateReportRow[];
}): { aoa: Cell[][]; headerRow: number; totalRow: number } {
  const aoa: Cell[][] = [
    [input.company || 'บริษัท'],
    [`แผนก: ${input.department}`],
    [`ช่วงวันที่: ${formatThaiDate(input.from)} – ${formatThaiDate(input.to)}`],
    [],
    sheetColumns(input.leaveColumns),
  ];
  const headerRow = aoa.length - 1;
  let late = 0;
  let lateMin = 0;
  let absent = 0;
  let leave = 0;
  const leaveByType = new Map<string, number>();
  let hasUncounted = false;
  for (const r of input.rows) {
    late += r.late_days;
    lateMin += r.late_minutes;
    absent += r.absent_days ?? 0;
    leave += r.leave_days ?? 0;
    if (r.absent_days == null || r.leave_days == null) hasUncounted = true;
    const typeCells = input.leaveColumns.map((c) => {
      if (r.leave_by_type == null) return '-';
      const days = r.leave_by_type[c.type] ?? 0;
      leaveByType.set(c.type, (leaveByType.get(c.type) ?? 0) + days);
      return days;
    });
    aoa.push([r.emp_code, r.name, r.late_days, r.late_minutes, r.absent_days ?? '-', ...typeCells, r.leave_days ?? '-']);
  }
  aoa.push([
    '', `รวม ${input.rows.length} คน`, late, lateMin, absent,
    ...input.leaveColumns.map((c) => round2(leaveByType.get(c.type) ?? 0)),
    round2(leave),
  ]);
  const totalRow = aoa.length - 1;
  aoa.push(
    [],
    ['หมายเหตุ'],
    ['สาย = จำนวนครั้ง และนาทีที่สาย ชุดเดียวกับหน้า "สรุปมาสาย"'],
    ['ขาด = วันทำงานที่ไม่มีสแกนเข้างานและไม่มีใบลาที่อนุมัติ (ไม่นับวันหยุด วันก่อนเริ่มงาน/หลังพ้นสภาพ วันนี้ที่ยังไม่จบวัน และวันที่ยังมาไม่ถึง) เกณฑ์เดียวกับสรุปเวลาเงินเดือน'],
    ['ลา = ใบลาที่อนุมัติแล้ว แยกตามประเภท ลาครึ่งวันนับ 0.5 · ใบลาที่ยังรออนุมัติ และวันลาที่ยังมาไม่ถึงยังไม่นับ · รวมลา = ผลรวมทุกประเภท'],
  );
  if (hasUncounted) aoa.push(['"-" = พนักงานที่ตั้งไม่ร่วมสรุปเวลา ระบบไม่นับขาด/ลาให้']);
  return { aoa, headerRow, totalRow };
}

export function summaryFileName(from: string, to: string): string {
  return `สรุปสาย-ขาด-ลา-${from}_${to}.xlsx`;
}

type XlsxModule = typeof import('xlsx');
export interface LateSummaryInput {
  company: string;
  from: string;
  to: string;
  leaveColumns: LeaveColumn[];
  rows: LateReportRow[];
}

/** สมุดงาน 1 ชีตต่อแผนก (แยกจากการดาวน์โหลดเพื่อเทสด้วยไลบรารีจริงได้) */
export function buildLateSummaryWorkbook(XLSX: XlsxModule, input: LateSummaryInput): ReturnType<XlsxModule['utils']['book_new']> {
  const groups = groupByDepartment(input.rows);
  const names = uniqueSheetNames(groups.map((g) => g.department));
  const lastCol = sheetColumns(input.leaveColumns).length - 1;
  const wb = XLSX.utils.book_new();
  groups.forEach((g, i) => {
    const { aoa, headerRow } = buildDepartmentSheet({
      company: input.company, department: g.department, from: input.from, to: input.to, leaveColumns: input.leaveColumns, rows: g.rows,
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 34 }, { wch: 11 }, { wch: 11 }, { wch: 10 }, ...input.leaveColumns.map(() => ({ wch: 16 })), { wch: 12 }];
    ws['!merges'] = [0, 1, 2].map((r) => ({ s: { r, c: 0 }, e: { r, c: lastCol } }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: headerRow, c: 0 }, e: { r: headerRow + g.rows.length, c: lastCol } }) };
    XLSX.utils.book_append_sheet(wb, ws, names[i]!);
  });
  return wb;
}

/** สร้างไฟล์และสั่งดาวน์โหลด — โหลด xlsx ตอนกดปุ่มเท่านั้น (แบบเดียวกับเทมเพลตเงินเดือน) */
export async function downloadLateSummaryXlsx(input: LateSummaryInput): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = buildLateSummaryWorkbook(XLSX, input);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  a.download = summaryFileName(input.from, input.to);
  a.click();
  URL.revokeObjectURL(a.href);
}
